import { isCompactFormat, expandCompactWorkflow } from '@/lib/compactWorkflow';

interface SectionReport {
  label: string;
  ok: boolean;
  errors: string[];
}

export interface ValidationReport {
  jsonSyntaxOk: boolean;
  jsonSyntaxError?: string;
  sections: SectionReport[];
  allOk: boolean;
}

/**
 * Run a detailed validation on raw JSON text and return a structured report
 * covering Blocks, Connectors, Groups, and Layer integrity.
 *
 * Validator version: multisheet-v2 (2026-04-23)
 *  - Detects multi-sheet root via Array.isArray(obj.sheets)
 *  - Per-sheet section labels: "Sheet N (name) — Blocks/Connectors/..."
 *  - Cross-sheet connector adjacency rule: abs(from-to) === 1
 */
export const VALIDATOR_VERSION = 'multisheet-v2';

export function validateWorkflowDetailed(jsonText: string): ValidationReport {
  // 1. JSON syntax
  let parsed: unknown;
  try {
    let raw = JSON.parse(jsonText);
    if (isCompactFormat(raw)) raw = expandCompactWorkflow(raw);
    parsed = raw;
  } catch (e) {
    const msg = e instanceof SyntaxError ? e.message : 'Okänt syntaxfel';
    return {
      jsonSyntaxOk: false,
      jsonSyntaxError: msg,
      sections: [],
      allOk: false,
    };
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return {
      jsonSyntaxOk: true,
      sections: [{
        label: 'Struktur',
        ok: false,
        errors: ['Rot-värdet måste vara ett JSON-objekt, inte en array eller primitivt värde.'],
      }],
      allOk: false,
    };
  }

  const obj = parsed as Record<string, unknown>;

  // Lean-format (Lager A): användar-export. Validera struktur ytligt
  // (sheets som array + obligatoriska id-fält på blocks/connectors/groups).
  // Strippade fält (description, icon, mtime osv) är medvetet utlämnade
  // → vi flagar dem inte som missing.
  if (obj.format === 'lean-1' && Array.isArray(obj.sheets)) {
    return validateLeanExport(obj);
  }

  // Multi-sheet branch: if `sheets` is an array, validate each sheet
  // independently and the cross-sheet connectors (adjacency rule).
  if (Array.isArray(obj.sheets)) {
    return validateMultiSheet(obj);
  }

  // 2. Validate each section independently (single-sheet legacy)
  const blocksReport = validateBlocks(obj.blocks);
  const connectorsReport = validateConnectors(obj.connectors, obj.blocks);
  const groupsReport = validateGroups(obj.groups, obj.blocks);
  const layersReport = validateLayers(obj.groups);

  const sections = [blocksReport, connectorsReport, groupsReport, layersReport];
  const allOk = sections.every(s => s.ok);

  return { jsonSyntaxOk: true, sections, allOk };
}

// ── Lean export (Lager A) ───────────────────────────────

function validateLeanExport(root: Record<string, unknown>): ValidationReport {
  const sections: SectionReport[] = [];
  const sheets = root.sheets as unknown[];
  if (sheets.length === 0) {
    sections.push({ label: 'Sheets', ok: false, errors: ['Inga sheets i lean-export.'] });
    return { jsonSyntaxOk: true, sections, allOk: false };
  }

  for (let i = 0; i < sheets.length; i++) {
    const s = sheets[i];
    const errs: string[] = [];
    if (!s || typeof s !== 'object') {
      sections.push({ label: `Sheet #${i + 1}`, ok: false, errors: ['Inte ett giltigt objekt.'] });
      continue;
    }
    const sheet = s as Record<string, unknown>;
    if (typeof sheet.id !== 'string') errs.push('Sheet saknar id.');
    if (!Array.isArray(sheet.blocks)) errs.push('Sheet.blocks måste vara array.');
    if (!Array.isArray(sheet.connectors)) errs.push('Sheet.connectors måste vara array.');
    if (!Array.isArray(sheet.groups)) errs.push('Sheet.groups måste vara array.');
    if (Array.isArray(sheet.blocks)) {
      for (const b of sheet.blocks) {
        if (!b || typeof b !== 'object') { errs.push('Block är inte ett objekt.'); continue; }
        const bl = b as Record<string, unknown>;
        if (typeof bl.id !== 'string') errs.push(`Block saknar id.`);
        if (typeof bl.x !== 'number' || typeof bl.y !== 'number') {
          errs.push(`Block ${bl.id ?? '?'} saknar numeriska x/y.`);
        }
      }
    }
    if (Array.isArray(sheet.connectors)) {
      for (const c of sheet.connectors) {
        if (!c || typeof c !== 'object') { errs.push('Connector är inte ett objekt.'); continue; }
        const cn = c as Record<string, unknown>;
        if (typeof cn.id !== 'string' || typeof cn.from !== 'string' || typeof cn.to !== 'string') {
          errs.push(`Connector saknar id/from/to.`);
        }
      }
    }
    sections.push({ label: `Sheet #${i + 1} (lean)`, ok: errs.length === 0, errors: errs });
  }

  return { jsonSyntaxOk: true, sections, allOk: sections.every(s => s.ok) };
}

// ── Multi-sheet ─────────────────────────────────────────

function validateMultiSheet(root: Record<string, unknown>): ValidationReport {
  const sections: SectionReport[] = [];
  const sheets = root.sheets as unknown[];

  if (sheets.length === 0) {
    sections.push({ label: 'Sheets', ok: false, errors: ['Inga sheets definierade — minst ett krävs.'] });
    return { jsonSyntaxOk: true, sections, allOk: false };
  }

  // Per-sheet validation, scoped labels.
  sheets.forEach((sheet, i) => {
    if (typeof sheet !== 'object' || sheet === null) {
      sections.push({ label: `Sheet #${i + 1}`, ok: false, errors: ['Inte ett giltigt objekt.'] });
      return;
    }
    const s = sheet as Record<string, unknown>;
    const sheetName = typeof s.name === 'string' ? s.name : `Sheet #${i + 1}`;
    const prefix = `Sheet ${i + 1} (${sheetName})`;

    const b = validateBlocks(s.blocks);
    const c = validateConnectors(s.connectors, s.blocks);
    const g = validateGroups(s.groups, s.blocks);
    const l = validateLayers(s.groups);

    sections.push({ label: `${prefix} — Blocks`, ok: b.ok, errors: b.errors });
    sections.push({ label: `${prefix} — Connectors`, ok: c.ok, errors: c.errors });
    sections.push({ label: `${prefix} — Groups`, ok: g.ok, errors: g.errors });
    sections.push({ label: `${prefix} — Layers`, ok: l.ok, errors: l.errors });
  });

  // Cross-sheet connectors: optional + adjacency rule (abs(from-to) === 1).
  const cscReport = validateCrossSheetConnectors(root.crossSheetConnectors, sheets);
  sections.push(cscReport);

  const allOk = sections.every(s => s.ok);
  return { jsonSyntaxOk: true, sections, allOk };
}

function validateCrossSheetConnectors(csc: unknown, sheets: unknown[]): SectionReport {
  const errors: string[] = [];
  if (csc === undefined || csc === null) {
    return { label: 'Cross-sheet connectors', ok: true, errors: [] };
  }
  if (!Array.isArray(csc)) {
    return { label: 'Cross-sheet connectors', ok: false, errors: ['Fältet "crossSheetConnectors" är inte en array.'] };
  }
  const sheetCount = sheets.length;
  const ids = new Set<string>();
  csc.forEach((c, i) => {
    const prefix = `CSC #${i + 1}`;
    if (typeof c !== 'object' || c === null) {
      errors.push(`${prefix}: Inte ett giltigt objekt.`); return;
    }
    const cc = c as Record<string, unknown>;
    if (typeof cc.id !== 'string' || !cc.id) {
      errors.push(`${prefix}: Saknar giltigt "id".`);
    } else if (ids.has(cc.id)) {
      errors.push(`${prefix} (${cc.id}): Dubblerat id.`);
    } else {
      ids.add(cc.id);
    }
    const from = cc.fromSheetIndex, to = cc.toSheetIndex;
    if (typeof from !== 'number' || typeof to !== 'number') {
      errors.push(`${prefix} (${cc.id || '?'}): fromSheetIndex/toSheetIndex måste vara nummer.`);
      return;
    }
    if (from < 0 || from >= sheetCount || to < 0 || to >= sheetCount) {
      errors.push(`${prefix} (${cc.id || '?'}): sheet-index utanför intervall (0..${sheetCount - 1}).`);
    }
    if (Math.abs(from - to) !== 1) {
      errors.push(`${prefix} (${cc.id || '?'}): cross-sheet får bara länka ANGRÄNSANDE sheets (abs diff = 1), fick ${Math.abs(from - to)}.`);
    }
    if (typeof cc.fromBlockId !== 'string' || typeof cc.toBlockId !== 'string') {
      errors.push(`${prefix} (${cc.id || '?'}): fromBlockId/toBlockId måste vara strängar.`);
    }
  });
  return { label: 'Cross-sheet connectors', ok: errors.length === 0, errors };
}

// ── Blocks ──────────────────────────────────────────────

function validateBlocks(blocks: unknown): SectionReport {
  const errors: string[] = [];

  if (!Array.isArray(blocks)) {
    return { label: 'Blocks', ok: false, errors: ['Fältet "blocks" saknas eller är inte en array.'] };
  }

  if (blocks.length === 0) {
    return { label: 'Blocks', ok: false, errors: ['Inga blocks definierade — minst ett block krävs.'] };
  }

  const ids = new Set<string>();

  blocks.forEach((block, i) => {
    const prefix = `Block #${i + 1}`;
    if (typeof block !== 'object' || block === null) {
      errors.push(`${prefix}: Inte ett giltigt objekt.`);
      return;
    }
    const b = block as Record<string, unknown>;

    // id
    if (!b.id || typeof b.id !== 'string') {
      errors.push(`${prefix}: Saknar giltigt "id".`);
    } else {
      if (ids.has(b.id)) errors.push(`${prefix} (${b.id}): Dubblerat id.`);
      ids.add(b.id);
    }

    // type
    if (!b.type || typeof b.type !== 'string') {
      errors.push(`${prefix} (${b.id || '?'}): Saknar "type".`);
    }

    // coordinates
    if (typeof b.x !== 'number' || !isFinite(b.x as number)) {
      errors.push(`${prefix} (${b.id || '?'}): Ogiltigt "x" värde.`);
    }
    if (typeof b.y !== 'number' || !isFinite(b.y as number)) {
      errors.push(`${prefix} (${b.id || '?'}): Ogiltigt "y" värde.`);
    }

    // config
    if (!b.config || typeof b.config !== 'object') {
      errors.push(`${prefix} (${b.id || '?'}): Saknar "config" objekt.`);
    } else {
      const cfg = b.config as Record<string, unknown>;
      if (!cfg.name || typeof cfg.name !== 'string' || (cfg.name as string).length === 0) {
        errors.push(`${prefix} (${b.id || '?'}): config.name saknas eller är tom.`);
      }
    }
  });

  return { label: 'Blocks', ok: errors.length === 0, errors };
}

// ── Connectors ──────────────────────────────────────────

function validateConnectors(connectors: unknown, blocks: unknown): SectionReport {
  const errors: string[] = [];

  if (connectors === undefined || connectors === null) {
    return { label: 'Connectors', ok: true, errors: [] }; // optional
  }

  if (!Array.isArray(connectors)) {
    return { label: 'Connectors', ok: false, errors: ['Fältet "connectors" är inte en array.'] };
  }

  // Collect valid block + group ids for reference checking
  const validIds = new Set<string>();
  if (Array.isArray(blocks)) {
    (blocks as Array<Record<string, unknown>>).forEach(b => {
      if (b && typeof b.id === 'string') validIds.add(b.id);
    });
  }
  // Groups are also valid targets — passed separately på call-site, vi
  // håller bara ID-set här.

  const ids = new Set<string>();
  const validPorts = new Set(['top', 'top-right', 'right', 'bottom-right', 'bottom', 'bottom-left', 'left', 'top-left']);

  connectors.forEach((conn, i) => {
    const prefix = `Connector #${i + 1}`;
    if (typeof conn !== 'object' || conn === null) {
      errors.push(`${prefix}: Inte ett giltigt objekt.`);
      return;
    }
    const c = conn as Record<string, unknown>;

    if (!c.id || typeof c.id !== 'string') {
      errors.push(`${prefix}: Saknar giltigt "id".`);
    } else {
      if (ids.has(c.id)) errors.push(`${prefix} (${c.id}): Dubblerat id.`);
      ids.add(c.id);
    }

    if (!c.fromId || typeof c.fromId !== 'string') {
      errors.push(`${prefix} (${c.id || '?'}): Saknar "fromId".`);
    }
    if (!c.toId || typeof c.toId !== 'string') {
      errors.push(`${prefix} (${c.id || '?'}): Saknar "toId".`);
    }

    // Self-loops (same-block from/to) är tillåtna som mönster — används
    // för state-machines — men kräver fromPort ≠ toPort så renderaren
    // kan teckna båglinjen. Se `findOrphanConnectors` nedan för
    // referensintegritet mot blocks.
    if (typeof c.fromId === 'string'
        && typeof c.toId === 'string'
        && c.fromId === c.toId
        && c.fromPort !== undefined
        && c.toPort !== undefined
        && c.fromPort === c.toPort) {
      errors.push(`${prefix} (${c.id || '?'}): self-loop saknar distinkta portar (fromPort=toPort=${String(c.fromPort)}).`);
    }

    // Referensintegritet: fromId / toId måste peka på ett existerande
    // block. Speglar DB-triggern `validate_workflow_connector_refs`
    // (migration 20260421160000) så UI:t fångar felet före save.
    if (typeof c.fromId === 'string' && validIds.size > 0 && !validIds.has(c.fromId)) {
      errors.push(`${prefix} (${c.id || '?'}): fromId "${c.fromId}" saknas i blocks.`);
    }
    if (typeof c.toId === 'string' && validIds.size > 0 && !validIds.has(c.toId)) {
      errors.push(`${prefix} (${c.id || '?'}): toId "${c.toId}" saknas i blocks.`);
    }

    // Port validation
    if (c.fromPort !== undefined && !validPorts.has(c.fromPort as string)) {
      errors.push(`${prefix} (${c.id || '?'}): Ogiltigt fromPort "${c.fromPort}".`);
    }
    if (c.toPort !== undefined && !validPorts.has(c.toPort as string)) {
      errors.push(`${prefix} (${c.id || '?'}): Ogiltigt toPort "${c.toPort}".`);
    }
  });

  return { label: 'Connectors', ok: errors.length === 0, errors };
}

/**
 * Returnerar alla connectors vars `fromId` eller `toId` inte finns i
 * `blocks`-arrayen. Ren funktion för snabba enhetstest + pre-save-
 * checkar i klienten. Speglar DB-triggern
 * `validate_workflow_connector_refs` (migration 20260421160000).
 *
 * Tom array = referensintegritet OK.
 */
export function findOrphanConnectors(
  connectors: Array<{ id: string; fromId: string; toId: string }>,
  blocks: Array<{ id: string }>,
): Array<{ id: string; fromId: string; toId: string; missing: 'from' | 'to' | 'both' }> {
  const validIds = new Set<string>();
  for (const b of blocks) {
    if (b && typeof b.id === 'string') validIds.add(b.id);
  }
  const orphans: Array<{ id: string; fromId: string; toId: string; missing: 'from' | 'to' | 'both' }> = [];
  for (const c of connectors) {
    if (!c) continue;
    const missingFrom = !validIds.has(c.fromId);
    const missingTo = !validIds.has(c.toId);
    if (missingFrom && missingTo) orphans.push({ ...c, missing: 'both' });
    else if (missingFrom) orphans.push({ ...c, missing: 'from' });
    else if (missingTo) orphans.push({ ...c, missing: 'to' });
  }
  return orphans;
}

// ── Groups ──────────────────────────────────────────────

function validateGroups(groups: unknown, blocks: unknown): SectionReport {
  const errors: string[] = [];

  if (groups === undefined || groups === null) {
    return { label: 'Groups', ok: true, errors: [] }; // optional
  }

  if (!Array.isArray(groups)) {
    return { label: 'Groups', ok: false, errors: ['Fältet "groups" är inte en array.'] };
  }

  const blockIds = new Set<string>();
  if (Array.isArray(blocks)) {
    (blocks as Array<Record<string, unknown>>).forEach(b => {
      if (b && typeof b.id === 'string') blockIds.add(b.id);
    });
  }

  const groupIds = new Set<string>();

  groups.forEach((group, i) => {
    const prefix = `Group #${i + 1}`;
    if (typeof group !== 'object' || group === null) {
      errors.push(`${prefix}: Inte ett giltigt objekt.`);
      return;
    }
    const g = group as Record<string, unknown>;

    if (!g.id || typeof g.id !== 'string') {
      errors.push(`${prefix}: Saknar giltigt "id".`);
    } else {
      if (groupIds.has(g.id)) errors.push(`${prefix} (${g.id}): Dubblerat group-id.`);
      groupIds.add(g.id);
    }

    const name = g.name ? ` "${g.name}"` : '';
    const label = `${prefix}${name} (${g.id || '?'})`;

    if (!Array.isArray(g.blockIds)) {
      errors.push(`${label}: "blockIds" saknas eller är inte en array.`);
    } else {
      (g.blockIds as string[]).forEach(bid => {
        if (!blockIds.has(bid)) {
          errors.push(`${label}: Refererar till okänt block "${bid}".`);
        }
      });
    }

    if (!g.color || typeof g.color !== 'string') {
      errors.push(`${label}: Saknar "color".`);
    }
  });

  return { label: 'Groups', ok: errors.length === 0, errors };
}

// ── Layers (parentGroupId integrity) ────────────────────

function validateLayers(groups: unknown): SectionReport {
  const errors: string[] = [];

  if (!Array.isArray(groups) || groups.length === 0) {
    return { label: 'Layers', ok: true, errors: [] };
  }

  const groupIds = new Set<string>();
  groups.forEach((g: Record<string, unknown>) => {
    if (g && typeof g.id === 'string') groupIds.add(g.id);
  });

  // Check parentGroupId references
  groups.forEach((group: unknown, i) => {
    if (!group || typeof group !== 'object') return;
    const g = group as Record<string, unknown>;
    const name = g.name ? ` "${g.name}"` : '';
    const label = `Group #${i + 1}${name} (${g.id || '?'})`;

    if (g.parentGroupId && typeof g.parentGroupId === 'string') {
      if (!groupIds.has(g.parentGroupId)) {
        errors.push(`${label}: parentGroupId "${g.parentGroupId}" refererar till en grupp som inte finns.`);
      }
      if (g.parentGroupId === g.id) {
        errors.push(`${label}: parentGroupId pekar på sig själv (cirkulär referens).`);
      }
    }
  });

  // Check for circular chains
  groups.forEach((group: unknown) => {
    if (!group || typeof group !== 'object') return;
    const g = group as Record<string, unknown>;
    if (!g.parentGroupId) return;

    const visited = new Set<string>();
    let current: string | undefined = g.id as string;
    while (current) {
      if (visited.has(current)) {
        const name = g.name ? ` "${g.name}"` : '';
        errors.push(`Group${name} (${g.id}): Cirkulär layer-kedja detekterad.`);
        break;
      }
      visited.add(current);
      const parent = groups.find((p: unknown) => (p as Record<string, unknown>)?.id === current);
      current = (parent as Record<string, unknown>)?.parentGroupId as string | undefined;
    }
  });

  // Deduplicate circular errors
  const unique = [...new Set(errors)];

  return { label: 'Layers', ok: unique.length === 0, errors: unique };
}
